import clsx from "clsx";
import { uniq } from "lodash";
import React, { Dispatch, FC, SetStateAction, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { hideMaterialsFilter } from "../../../features-config.json";
import { MaterialType } from "../../types/materials";
import { Button } from "../buttons/button";
import { Input, Select } from "../forms";
import { MaterialsFormFields } from "../forms/forms-fields/materials-form-fields";
import {
  MaterialOption,
  MaterialsFormFieldsLabels,
} from "../forms/forms-fields/materials-form-fields/types";
import { Icon } from "../icon";
import { EMPTY_OPTION_VALUE } from "../strips/product-finder";
import { ExhibitionFinderFiltersFormTypes } from "./types";

export interface ExhibitionFiltersPanelProps {
  labels: {
    "exhibition-finder-panel-clear-filters": string;
    "exhibition-finder-panel-apply-filters": string;
    "exhibition-finder-panel-option-all": string;
    "exhibition-finder-panel-location": string;
    "exhibition-finder-panel-future-or-ongoing-exhibitions": string;
    "exhibition-finder-panel-past-exhibitions": string;
    "exhibition-finder-panel-search": string;
  } & MaterialsFormFieldsLabels;
  exhibitions: Queries.ExhibitionCardDataFragment[];
  isOpen: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export const ExhibitionFiltersPanel: FC<ExhibitionFiltersPanelProps> = ({
  labels,
  isOpen,
  setOpen,
  exhibitions,
}) => {
  const {
    watch,
    register,
    setValue,
    formState: { isValid },
  } = useFormContext<ExhibitionFinderFiltersFormTypes>();

  const location = watch("location");
  const locations = useMemo(() => {
    return uniq(exhibitions.map((e) => e.location));
  }, [exhibitions]);

  return (
    <div
      className={clsx(
        "transition-all duration-300 rounded-3xl",
        "bg-neutral-light overflow-hidden w-full relative",
        isOpen ? "max-h-[200vh] overflow-y-auto" : "max-h-0"
      )}
    >
      <div className="overflow-y-auto p-4">
        {isOpen && (
          <button className="absolute right-3 top-3" type="button" onClick={() => setOpen(false)}>
            <Icon icon="close" className="w-6 h-6" />
          </button>
        )}
        <div
          className={clsx(
            "w-full h-full pt-3 pb-2 flex flex-col md:flex-row md:items-start gap-6 lg:gap-12"
          )}
        >
          {!hideMaterialsFilter && (
            <MaterialsFormFields
              labels={labels}
              materialsToShow={Object.values(MaterialType) as MaterialOption[]}
            />
          )}
          <Select
            label={labels["exhibition-finder-panel-location"]}
            value={location}
            onChange={(e) => setValue("location", e.target.value)}
            className="flex-none"
          >
            <option value={EMPTY_OPTION_VALUE}>
              {labels["exhibition-finder-panel-option-all"]}
            </option>
            {locations.map(
              (location, i) =>
                location && (
                  <option key={i} value={location}>
                    {location}
                  </option>
                )
            )}
          </Select>

          <Input
            label={labels["exhibition-finder-panel-search"]}
            startDecoration={<Icon icon="search" className="w-5" />}
            {...register("search")}
          />
        </div>
      </div>

      <div className="w-full flex justify-end pr-4 pb-4">
        <Button variant="primary" size="sm" type="submit" disabled={!isValid}>
          {labels["exhibition-finder-panel-apply-filters"]}
        </Button>
      </div>
    </div>
  );
};
